/*
 * The rules behind the Marketing tabs the WordPress Steward had and the suite did not: the Dashboard, Channels, Due
 * again, Email list, Guest posts and the posts calendar's holidays (15-marketing.js, dueagain.php, signups.php). Small
 * pure functions, so the server, the screens and the preview agree.
 */
import type { Trade } from "./core.js";
import type { Channel } from "./marketing-suite.js";
import { addDays, daysBetween } from "./schedule.js";

// Channels ------------------------------------------------------------------------------------------------------------

/** The periods the old Channels tab offered (INS_RANGES in 17-insights.js). */
export const CHANNEL_RANGES = [
  ["month", "This month"],
  ["last", "Last month"],
  ["30", "Past 30 days"],
  ["90", "Past 90 days"],
  ["year", "This year"],
  ["all", "All time"],
] as const;
export type ChannelRange = (typeof CHANNEL_RANGES)[number][0];

/** The first and last day of a period, from the company's today. All time starts in 2000. */
export function rangeDays(range: ChannelRange, today: string): { from: string; to: string } {
  const month = today.slice(0, 7);
  if (range === "month") return { from: `${month}-01`, to: today };
  if (range === "last") {
    const first = `${month}-01`;
    const end = addDays(first, -1);
    return { from: `${end.slice(0, 7)}-01`, to: end };
  }
  if (range === "30") return { from: addDays(today, -29), to: today };
  if (range === "90") return { from: addDays(today, -89), to: today };
  if (range === "year") return { from: `${today.slice(0, 4)}-01-01`, to: today };
  return { from: "2000-01-01", to: today };
}

/** The channels a brand always sees, even at 0: cold email everywhere, door hangers and yard signs for lawn care. */
export function alwaysChannels(trade: Trade): Channel[] {
  return ["lsa", "search", "facebook", "email", ...(trade === "lawn" ? (["doorhanger", "yard"] as const) : [])];
}

/** Money per thing (per lead, per new client), or null when there is no spend or nothing to divide by. */
export function perCents(spendCents: number, n: number): number | null {
  return spendCents > 0 && n > 0 ? Math.round(spendCents / n) : null;
}

// Dashboard ------------------------------------------------------------------------------------------------------------

/** The Google Maps spot over every tracked search: the average of each search's newest average, and the change. */
export function mapsSpot(grids: readonly { now: number | null; before: number | null }[]): {
  average: number | null;
  searches: number;
  /** Positive means the spot got better (a lower number). Null when not every search has a scan before. */
  better: number | null;
} {
  const done = grids.filter((g): g is { now: number; before: number | null } => g.now !== null);
  if (done.length === 0) return { average: null, searches: 0, better: null };
  const avg = (xs: number[]) => Math.round((xs.reduce((a, x) => a + x, 0) / xs.length) * 10) / 10;
  const average = avg(done.map((g) => g.now));
  const was = done.filter((g): g is { now: number; before: number } => g.before !== null);
  const better = was.length === done.length ? Math.round((avg(was.map((g) => g.before)) - average) * 10) / 10 : null;
  return { average, searches: done.length, better };
}

export type NextStep = "posts" | "reviews" | null;

/** The one next step on the Dashboard (mkDashHTML): plan posts when none are coming, else turn review requests on. */
export function nextStep(upcomingPosts: number, reviewsOn: boolean): NextStep {
  if (upcomingPosts === 0) return "posts";
  if (!reviewsOn) return "reviews";
  return null;
}

// Due again ------------------------------------------------------------------------------------------------------------

/** Days until each kind of work is due again, to start from (dueagain.php). 0 is never. Names match the job names. */
export const DUE_AGAIN_DEFAULTS: Record<Trade, Record<string, number>> = {
  cleaning: { "Deep House Cleaning": 90, "Standard House Cleaning": 45 },
  lawn: { "Lawn Cleanup": 180, "Mulch Installation": 365, "Hedge Trimming": 120, "Weed Control": 90 },
  washers: {
    "Window Cleaning": 180,
    "Roof Cleaning": 730,
    "Gutter Cleaning": 180,
    "House Soft Wash": 365,
    "Pressure Washing": 365,
    "Fence Cleaning": 365,
  },
  agency: {},
  hq: {},
  client: {},
};
export const DUE_AGAIN_MAX_DAYS = 3650;
export const DUE_AGAIN_TEXT =
  "Hi {first}, it's {business}. It has been {months} since your {service}. Want us to come back? Reply here and we'll find a day.";

/** The days for a kind of work: what the owner saved, else the trade's default, else never (0). */
export function dueDays(kind: string, saved: Readonly<Record<string, number>>, trade: Trade): number {
  return saved[kind] ?? DUE_AGAIN_DEFAULTS[trade][kind] ?? 0;
}

/** "a month", "5 months", "a year", "2 years": the time since the last visit, as the old text said it. */
export function monthsWords(last: string, today: string): string {
  const m = Math.max(1, Math.round(daysBetween(last, today) / 30.4));
  if (m >= 12 && m % 12 === 0) return m === 12 ? "a year" : `${String(m / 12)} years`;
  return m === 1 ? "a month" : `${String(m)} months`;
}

/** "12 days ago", "4 months ago", "1.5 years ago" (30-dueagain.js). */
export function sinceWords(last: string, today: string): string {
  const n = daysBetween(last, today);
  const ago = (x: number, one: string) => `${String(x)} ${x === 1 ? one : `${one}s`} ago`;
  if (n < 45) return ago(n, "day");
  if (n < 365) return ago(Math.round(n / 30.4), "month");
  return ago(Math.round(n / 36.5) / 10, "year");
}

/** The due-again text for one client, from the words with their fill-ins. */
export function dueAgainWords(
  text: string,
  r: { first: string | null; service: string; last: string; today: string; business: string },
): string {
  return text
    .replaceAll("{first}", r.first?.trim() ? r.first.trim() : "there")
    .replaceAll("{service}", r.service.toLowerCase())
    .replaceAll("{months}", monthsWords(r.last, r.today))
    .replaceAll("{business}", r.business);
}

/** Whether a kind of work last done on `last` is due by `today`, and by how many days. */
export function dueState(last: string, days: number, today: string): { due: boolean; dueOn: string; over: number } {
  const dueOn = addDays(last, days);
  return { due: days > 0 && dueOn <= today, dueOn, over: daysBetween(dueOn, today) };
}

// Posts calendar holidays ----------------------------------------------------------------------------------------------

type When = [number, number | string] | "easter" | "mardigras" | "smallbiz";
type Ideas = Partial<Record<"cleaning" | "lawn" | "washers", string>> & { general: string };

/** Holidays worth a post, with an idea for each trade (HOLIDAYS in 15-marketing.js). "3-1" is the 3rd Monday, "L-1" the last. */
export const HOLIDAYS: [string, string, When, Ideas][] = [
  [
    "newyear",
    "New Year's Day",
    [1, 1],
    {
      cleaning: "A fresh start. A deep clean to begin the year, or set up repeat cleaning so the house stays that way.",
      lawn: "Plan the year's yard care now and get on the spring schedule before it fills up.",
      washers: "Start the year with clean windows and a washed driveway after the holiday traffic.",
      general: "A fresh start post. What you help people get done this year.",
    },
  ],
  ["mlk", "Martin Luther King Jr. Day", [1, "3-1"], { general: "A short community post. Skip the sales pitch today." }],
  [
    "valentine",
    "Valentine's Day",
    [2, 14],
    {
      cleaning: "Give a cleaning as the gift, or get the house clean before date night.",
      lawn: "Flower beds cleaned up and ready for spring planting.",
      washers: "Give a house wash or window cleaning as the gift.",
      general: "A gift idea post. Your service as the gift.",
    },
  ],
  [
    "mardigras",
    "Mardi Gras",
    "mardigras",
    {
      cleaning: "Guests in town for Carnival? Clean before they come and after the parades.",
      lawn: "Parade route yards. Cleanup after the parades, and the winter rye at its greenest.",
      washers: "Beads, grease and parade grime. Driveway and sidewalk washing after Carnival.",
      general: "A Carnival post. Getting ready for guests, and the cleanup after.",
    },
  ],
  [
    "dststart",
    "Clocks spring forward",
    [3, "2-0"],
    {
      cleaning: "Clocks change. Test the smoke detectors, dust the vents and ceiling fans.",
      lawn: "More daylight, and mowing season starts. Get on the weekly schedule.",
      washers: "Longer days. Get the spring washing on the calendar before pollen season.",
      general: "A seasonal reminder post. One thing to check when the clocks change.",
    },
  ],
  [
    "stpatrick",
    "St. Patrick's Day",
    [3, 17],
    {
      cleaning: "Spring cleaning season starts. A plain checklist of what a spring clean covers.",
      lawn: "When St. Augustine wakes up, and what to do for the first green up.",
      washers: "The green on your siding and walkways is mold. Here's how we wash it off.",
      general: "A green themed post that ties into your service.",
    },
  ],
  [
    "spring",
    "First day of spring",
    [3, 20],
    {
      cleaning: "Spring cleaning. The spots a regular clean skips, like baseboards, blinds and inside the oven.",
      lawn: "First cut, weeds and the first feeding of the year.",
      washers: "Pollen season. Windows and screens washed once the yellow dust settles.",
      general: "A spring checklist post.",
    },
  ],
  [
    "easter",
    "Easter",
    "easter",
    {
      cleaning: "Hosting Easter lunch? Book the deep clean for the week before.",
      lawn: "Egg hunt ready. Mow and edge the week before.",
      washers: "Patio and porch washed before Easter guests.",
      general: "A holiday post. Getting ready for family.",
    },
  ],
  [
    "earthday",
    "Earth Day",
    [4, 22],
    {
      cleaning: "What you clean with and how you cut waste on every visit.",
      lawn: "Mulching, leaving clippings on the lawn, and watering early in the day.",
      washers: "How you protect plants and handle runoff while washing.",
      general: "One thing your business does to waste less.",
    },
  ],
  [
    "mother",
    "Mother's Day",
    [5, "2-0"],
    {
      cleaning: "Give Mom a cleaning. A gift she'll use.",
      lawn: "Fresh flower beds and a clean yard for Mom.",
      washers: "Give Mom a house wash or clean windows.",
      general: "A gift idea post for Mother's Day.",
    },
  ],
  [
    "memorial",
    "Memorial Day",
    [5, "L-1"],
    { general: "A thank you post. Then get ready for summer, with cookouts, guests and longer days." },
  ],
  [
    "hurricane",
    "Hurricane season starts",
    [6, 1],
    {
      cleaning: "A storm prep checklist, and that you help with cleanup after.",
      lawn: "Trim limbs away from the house and clear the drains before storm season.",
      washers: "Clean windows and siding now so it's easier to spot damage after a storm.",
      general: "A storm season checklist post.",
    },
  ],
  [
    "father",
    "Father's Day",
    [6, "3-0"],
    {
      cleaning: "Give Dad a weekend off. Gift a cleaning.",
      lawn: "Give Dad the weekend off from mowing.",
      washers: "Give Dad the weekend off from the pressure washer.",
      general: "A gift idea post for Father's Day.",
    },
  ],
  [
    "july4",
    "Fourth of July",
    [7, 4],
    {
      cleaning: "Before the cookout, the kitchen and guest bathroom. After, the cleanup.",
      lawn: "A fresh cut and edge before the backyard party.",
      washers: "Patio, deck and driveway washed before the Fourth.",
      general: "A holiday post. Getting ready for guests.",
    },
  ],
  ["labor", "Labor Day", [9, "1-1"], { general: "A thank you to your team. Show the people who do the work." }],
  [
    "fall",
    "First day of fall",
    [9, 22],
    {
      cleaning: "Fall cleaning before the holidays. What to do in October so November is easy.",
      lawn: "Overseeding season is coming. Winter rye goes down in October and November.",
      washers: "Wash the house and windows before the holidays.",
      general: "A fall checklist post.",
    },
  ],
  [
    "halloween",
    "Halloween",
    [10, 31],
    {
      cleaning: "Hosting a party? Book the cleanup for the morning after.",
      lawn: "Curb appeal before the trick or treaters walk up your path.",
      washers: "Front walk and porch washed before trick or treaters.",
      general: "A Halloween post tied to your service.",
    },
  ],
  [
    "dstend",
    "Clocks fall back",
    [11, "1-0"],
    {
      cleaning: "Clocks change. Test the smoke detectors, dust the vents and ceiling fans.",
      lawn: "Shorter days. Fall leaf cleanup and the last cuts of the year.",
      washers: "Less daylight. Get the outside washed before the holidays.",
      general: "A seasonal reminder post. One thing to check when the clocks change.",
    },
  ],
  ["veterans", "Veterans Day", [11, 11], { general: "A thank you to veterans, on your team or among your clients." }],
  [
    "thanksgiving",
    "Thanksgiving",
    [11, "4-4"],
    {
      cleaning: "Deep clean before the guests, cleanup after. Book the week before early.",
      lawn: "Leaves cleaned up before family arrives.",
      washers: "Clean windows for the holiday photos.",
      general: "A thank you post to your clients.",
    },
  ],
  [
    "smallbiz",
    "Small Business Saturday",
    "smallbiz",
    { general: "Tell your story. Who you are, where you work, and thank your local clients." },
  ],
  [
    "christmas",
    "Christmas",
    [12, 25],
    {
      cleaning: "A holiday greeting. Post the week before about a clean house for guests.",
      lawn: "A holiday greeting, and a yard ready for guests.",
      washers: "A holiday greeting, and clean windows before the lights go up.",
      general: "A holiday greeting post.",
    },
  ],
  [
    "nye",
    "New Year's Eve",
    [12, 31],
    {
      cleaning: "Book the after party cleanup for New Year's Day.",
      lawn: "Your year in photos. The best yards you did.",
      washers: "Your year in before and afters.",
      general: "A year in review post.",
    },
  ],
];

const ymd = (y: number, m: number, d: number) => `${String(y)}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
/** 0 is Sunday. */
const weekday = (day: string) => new Date(`${day}T12:00:00Z`).getUTCDay();

/** Easter Sunday (the Western church's date, the old easterDate). */
export function easterDate(y: number): string {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mo = Math.floor((h + l - 7 * m + 114) / 31);
  const da = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(y, mo, da);
}

/** The day a holiday falls on in a year. */
export function holidayDate(y: number, when: When): string {
  if (when === "easter") return easterDate(y);
  if (when === "mardigras") return addDays(easterDate(y), -47);
  if (when === "smallbiz") return addDays(holidayDate(y, [11, "4-4"]), 2);
  const [mo, day] = when;
  if (typeof day === "number") return ymd(y, mo, day);
  const [n, wd] = day.split("-");
  const dow = Number(wd);
  if (n === "L") {
    let d = addDays(mo === 12 ? ymd(y + 1, 1, 1) : ymd(y, mo + 1, 1), -1);
    while (weekday(d) !== dow) d = addDays(d, -1);
    return d;
  }
  let d = ymd(y, mo, 1);
  while (weekday(d) !== dow) d = addDays(d, 1);
  return addDays(d, 7 * (Number(n) - 1));
}

export interface HolidayOn {
  key: string;
  name: string;
  day: string;
  /** What to post about, for this trade. */
  idea: string;
}

/** The holidays in a month ("2026-11"), with the trade's post idea, in date order. */
export function holidaysIn(month: string, trade: Trade): HolidayOn[] {
  const y = Number(month.slice(0, 4));
  const pick = trade === "cleaning" || trade === "lawn" || trade === "washers" ? trade : null;
  return HOLIDAYS.map(([key, name, when, ideas]) => ({
    key,
    name,
    day: holidayDate(y, when),
    idea: (pick ? ideas[pick] : undefined) ?? ideas.general,
  }))
    .filter((h) => h.day.startsWith(month))
    .sort((a, b) => a.day.localeCompare(b.day));
}

/** The days a month calendar shows, Sunday to Saturday weeks from the week the month starts to the week it ends. */
export function monthDays(month: string): string[] {
  let d = `${month}-01`;
  while (weekday(d) !== 0) d = addDays(d, -1);
  const out: string[] = [];
  do {
    out.push(d);
    d = addDays(d, 1);
  } while (!(weekday(d) === 0 && d.slice(0, 7) > month));
  return out;
}

/** The month before or after ("2026-11", 1 is "2026-12"). */
export function shiftMonth(month: string, by: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + by;
  const yy = y + Math.floor(m / 12);
  const mm = ((m % 12) + 12) % 12;
  return `${String(yy)}-${String(mm + 1).padStart(2, "0")}`;
}

// Email list and guest posts -------------------------------------------------------------------------------------------

export const PITCH_STATUSES = ["new", "yes", "no"] as const;
export type PitchStatus = (typeof PITCH_STATUSES)[number];
export const PITCH_WORDS: Record<PitchStatus, string> = { new: "New", yes: "Said yes", no: "Said no" };

/** Whether a signup's ZIP is in the company's areas: in, out, or unknown when it has no ZIP or no areas are set. */
export function signupArea(zip: string | null, zips: ReadonlySet<string>): "in" | "out" | "unknown" {
  const z = zip?.slice(0, 5);
  if (!z || zips.size === 0) return "unknown";
  return zips.has(z) ? "in" : "out";
}
